import React, { useEffect, useState, useRef, useCallback } from 'react';
import Sidebar from '../Sidebar/Sidebar';
import Welcome from '../Welcome/Welcome';
import Settings from '../Pages/Settings/Settings';
import Stories from '../Pages/Stories/Stories';
import ProductList from '../Pages/ProductList/ProductList';
import Categories from '../Pages/Categories/Categories';
import BannerProducts from '../Pages/BannerProducts/BannerProducts';
import './Dashboard.css';
import UserOrderReport from '../Pages/UserOrderReport/user-order-report';
import CategoryReport from '../Pages/CategoryReport/category-report';
import CouponPage from '../Pages/Coupon/Coupon';
import WhatsApp from '../Pages/WhatsApp/WhatsApp';
import WhatsAppChat from '../Pages/WhatsAppChat/WhatsAppChat';
import Notification from '../Pages/Notification/Notification';
import EventLogs from '../Pages/EventLogs/EventLogs';
import AdminList from '../Pages/AdminList/AdminList';
import Users from '../Pages/Users/Users';
import Orders from '../Pages/Orders/Orders';
import { useLocation, useNavigate } from 'react-router-dom';
import ReelVideos from '../Pages/ReelVideos/ReelVideos';
import CrmSummary from '../Pages/CrmSummary/CrmSummary';
import Analytics from '../Pages/Analytics/Analytics';
import AppSearches from '../Pages/Analytics/AppSearches';
import DispatchPlanner from '../Pages/DispatchPlanner/DispatchPlanner';
import Drivers from '../Pages/Drivers/Drivers';
import DispatchHistory from '../Pages/DispatchHistory/DispatchHistory';
import ServerError from '../Pages/ServerError/ServerError';
import ChatSupport from '../Pages/ChatSupport/ChatSupport';
import ChatWindow from '../Pages/ChatSupport/Chat/ChatWindow';
import SeoPage from '../Pages/Seo/SeoPage';
import SpinWheelSettings from '../Pages/SpinWheel/SpinWheelSettings';
import AppTester from '../Pages/AppTester/AppTester';
import AdminApp from '../Pages/AdminApp/AdminApp';
import { orderAPI } from '../../config/order-api';
import { authAPI } from '../../config/auth-api';
import { toast } from 'react-toastify';

const Dashboard = ({ onLogout, adminData, setAdminData }) => {
  const [currentPage, setCurrentPage] = useState('welcome');
  const [dispatchHasUnsavedChanges, setDispatchHasUnsavedChanges] = useState(false);
  const [pendingPage, setPendingPage] = useState(null);
  const [showUnsavedNavigationModal, setShowUnsavedNavigationModal] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [refreshChatTrigger, setRefreshChatTrigger] = useState({ count: 0, reset: false });
  const [refreshTriggers, setRefreshTriggers] = useState({});

  const pendingAudioRef = useRef(null);
  const handlePageChangeRef = useRef(null);
  const currentPageRef = useRef(currentPage);

  const handleChatUpdate = () => {
    setRefreshChatTrigger(prev => ({ count: (prev?.count || 0) + 1, reset: false }));
  };

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.page) {
      const page = location.state.page === 'custom-orders' ? 'orders' : location.state.page;
      setCurrentPage(page);
    }
  }, [location.state]);

  const playAudio = useCallback((fileName, onEnded = null) => {
    const audio = new Audio(`/alerts/${fileName}`);
    if (onEnded) audio.addEventListener('ended', onEnded);
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        pendingAudioRef.current = { fileName, onEnded };
      });
    }
  }, []);

  useEffect(() => {
    const handleFirstInteraction = () => {
      if (pendingAudioRef.current) {
        const { fileName, onEnded } = pendingAudioRef.current;
        pendingAudioRef.current = null;
        const audio = new Audio(`/alerts/${fileName}`);
        if (onEnded) audio.addEventListener('ended', onEnded);
        audio.play().catch(e => console.error('Audio play failed after interaction:', e));
      }
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('keydown', handleFirstInteraction);

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };
  }, []);

  const handlePageChange = (page, resetFilters = false, state = {}) => {
    if (currentPage === 'dispatch-planner' && dispatchHasUnsavedChanges && page !== 'dispatch-planner') {
      setPendingPage(page);
      setShowUnsavedNavigationModal(true);
      return;
    }

    if (currentPage === page) {
      setRefreshTriggers(prev => ({
        ...prev,
        [page]: { count: (prev[page]?.count || 0) + 1, reset: resetFilters }
      }));
      return;
    }

    navigate('/dashboard', { state: { ...state, page } });
    setCurrentPage(page);
  };

  useEffect(() => {
    handlePageChangeRef.current = handlePageChange;
  });

  const handleAlerts = useCallback((data, currentAdminData) => {
    const { order, enquiry, message, whatsappMessage, serverError } = data;

    const messageAlertEnabled = currentAdminData?.webAlerts?.messageAlert !== false;

    const alertConfig = [
      { key: 'order', count: order, enabled: currentAdminData?.webAlerts?.orderAlert !== false },
      { key: 'message', count: message, enabled: messageAlertEnabled },
      { key: 'whatsappMessage', count: whatsappMessage, enabled: messageAlertEnabled },
      { key: 'enquiry', count: enquiry, enabled: currentAdminData?.webAlerts?.enquiryAlert !== false },
      { key: 'serverError', count: serverError, enabled: currentAdminData?.webAlerts?.serverErrorAlert !== false },
    ];

    const activeAlerts = alertConfig.filter(a => a.count > 0 && a.enabled && a.key !== 'serverError');
    const playError = serverError > 5 && currentAdminData?.webAlerts?.serverErrorAlert !== false;

    if (activeAlerts.length > 0) {
      activeAlerts.forEach(({ key, count }) => {
        const label = key === 'whatsappMessage'
          ? (count > 1 ? 'WhatsApp messages' : 'WhatsApp message')
          : key === 'enquiry'
            ? (count > 1 ? 'enquiries' : 'enquiry')
            : (count > 1 ? `${key}s` : key);

        const targetPage = key === 'message'
          ? 'chat-support'
          : key === 'whatsappMessage'
            ? 'whatsapp-chat'
            : 'orders';
        const isEnquiry = key === 'enquiry';

        toast.info(`${count} new ${label}`, {
          ...(isEnquiry ? {} : {
            onClick: () => handlePageChangeRef.current(targetPage, true),
            style: { cursor: 'pointer' },
          }),
        });

        if (!isEnquiry && currentPageRef.current === targetPage) {
          setRefreshTriggers(prev => ({
            ...prev,
            [targetPage]: { count: (prev[targetPage]?.count || 0) + 1, reset: false }
          }));
        }
      });

      const soundKeys = [...new Set(activeAlerts.map((alert) => (
        alert.key === 'whatsappMessage' ? 'message' : alert.key
      )))];
      const fileName = soundKeys.join('+') + '.mp3';
      const onEnded = playError
        ? () => {
          toast.error(`⚠️ ${serverError} server errors detected`, {
            onClick: () => handlePageChangeRef.current('server-errors', true),
            style: { cursor: 'pointer' },
          });
          playAudio('error_alert.mp3');
        }
        : null;
      playAudio(fileName, onEnded);
    } else if (playError) {
      toast.error(`⚠️ ${serverError} server errors detected`, {
        onClick: () => handlePageChangeRef.current('server-errors', true),
        style: { cursor: 'pointer' },
      });
      playAudio('error_alert.mp3');
    }

    if (playError && currentPageRef.current === 'server-errors') {
      setRefreshTriggers(prev => ({
        ...prev,
        'server-errors': { count: (prev['server-errors']?.count || 0) + 1, reset: false }
      }));
    }

  }, [playAudio]);

  useEffect(() => {
    const pollAlerts = async () => {
      try {
        const response = await orderAPI.getAdminPolling();
        if (response.success && response.data) {
          const currentAdminData = authAPI.getAdminData();
          handleAlerts(response.data, currentAdminData);
        }
      } catch (error) {
        console.error('Polling failed:', error);
      }
    };

    pollAlerts();
    const pollingInterval = setInterval(pollAlerts, 30000); // 30 seconds

    return () => clearInterval(pollingInterval);
  }, [handleAlerts]);

  const confirmNavigateAway = () => {
    setShowUnsavedNavigationModal(false);
    setDispatchHasUnsavedChanges(false);
    if (pendingPage) {
      setCurrentPage(pendingPage);
      setPendingPage(null);
    }
  };

  const cancelNavigateAway = () => {
    setShowUnsavedNavigationModal(false);
    setPendingPage(null);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'welcome':
        return <Welcome />;
      case 'settings':
        return <Settings adminData={adminData} />;
      case 'stories':
        return <Stories adminData={adminData} />;
      case 'seo':
        return <SeoPage adminData={adminData} />;
      case 'spin-wheel':
        return <SpinWheelSettings adminData={adminData} />;
      case 'product-list':
        return <ProductList adminData={adminData} productFilter={location.state} />;
      case 'categories':
        return <Categories adminData={adminData} />;
      case 'banner-products':
        return <BannerProducts adminData={adminData} />;
      case 'user-order-report':
        return <UserOrderReport />;
      case 'category-report':
        return <CategoryReport />;
      case 'coupon':
        return <CouponPage adminData={adminData} />;
      case 'whatsapp':
        return <WhatsApp adminData={adminData} />;
      case 'whatsapp-chat':
        return <WhatsAppChat adminData={adminData} />;
      case 'notification':
        return <Notification adminData={adminData} />;
      case 'event-logs':
        return <EventLogs incomingFilter={location.state} />;
      case 'admins':
        return <AdminList adminData={adminData} setAdminData={setAdminData} />;
      case 'users':
        return <Users userFilter={location.state} adminData={adminData} />;
      case 'orders':
        return <Orders
          userFilter={location.state}
          adminData={adminData}
          refreshTrigger={refreshTriggers['orders'] || 0}
        />;
      case 'reel-videos':
        return <ReelVideos adminData={adminData} />;
      case 'crm-summary':
        return <CrmSummary />;
      case 'analytics':
        return <Analytics />;
      case 'website-analytics':
        return <Analytics source="website" />;
      case 'app-searches':
        return <AppSearches />;
      case 'dispatch-planner':
        return <DispatchPlanner onUnsavedChange={setDispatchHasUnsavedChanges} adminData={adminData} onPageChange={handlePageChange} />;
      case 'drivers':
      case 'delivery-staff':
        return <Drivers adminData={adminData} onPageChange={handlePageChange} type="delivery" />;
      case 'packing-staff':
        return <Drivers adminData={adminData} onPageChange={handlePageChange} type="packing" />;
      case 'admin-app':
        return <AdminApp adminData={adminData} />;
      case 'dispatch-history':
        return <DispatchHistory adminData={adminData} onPageChange={handlePageChange} />;
      case 'server-errors':
        return <ServerError refreshTrigger={refreshTriggers['server-errors'] || 0} />;
      case 'app-tester':
        return <AppTester />;
      case 'chat-support':
        return (
          <ChatSupport
            onChatSelect={setSelectedChat}
            selectedChatId={selectedChat?._id}
            refreshTrigger={refreshChatTrigger || refreshTriggers['chat-support'] || 0}
          />
        );
      default:
        return <Welcome />;
    }
  };

  return (
    <div className="dashboard">
      <Sidebar
        onLogout={onLogout}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        adminData={adminData}
        setAdminData={setAdminData}
      />
      <main
        className={selectedChat ? 'dashboard-main chat-open' : 'dashboard-main'}
        style={
          selectedChat
            ? { marginRight: '380px', transition: 'margin-right 0.22s ease' }
            : { transition: 'margin-right 0.22s ease' }
        }
      >
        <div className="dashboard-content">{renderPage()}</div>
      </main>

      {selectedChat && (
        <ChatWindow
          chat={selectedChat}
          onClose={() => setSelectedChat(null)}
          onChatUpdate={handleChatUpdate}
          adminData={adminData}
        />
      )}

      {showUnsavedNavigationModal && (
        <div className="dashboard-modal-overlay">
          <div className="dashboard-modal">
            <h3>Unsaved Changes</h3>
            <p>Are you sure you want to exit without saving changes?</p>
            <div className="dashboard-modal-actions">
              <button className="dashboard-modal-cancel" onClick={cancelNavigateAway}>
                Cancel
              </button>
              <button className="dashboard-modal-confirm" onClick={confirmNavigateAway}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;